import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Card } from "@/shared/components/ui/Card"
import {
  FormField,
  FormSection,
  NumericInput,
  TextAreaField,
  DateTimeField,
  ImageUploadField,
  BooleanToggle,
  CheckboxCard,
} from "@/shared/components/forms"
import { maintenanceSchema, type MaintenanceFormData } from "../schemas/maintenanceSchema"

interface MaintenanceFormProps {
  defaultValues?: Partial<MaintenanceFormData>
  onSubmit: (data: MaintenanceFormData) => Promise<void>
  isLoading: boolean
}

/**
 * Form for creating/editing maintenance checks.
 */
export function MaintenanceForm({ defaultValues, onSubmit, isLoading }: MaintenanceFormProps) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<MaintenanceFormData>({
    resolver: zodResolver(maintenanceSchema),
    defaultValues: {
      checkedAt: new Date(),
      waterLevel: undefined,
      waterFlowCheck: true,
      paddleWheelFunctioning: true,
      stressedFishAtInlets: false,
      algalBloom: false,
      presenceOfBirds: false,
      procedureFollowed: "",
      dischargePh: undefined,
      dischargeDo: undefined,
      dischargeFlowRate: undefined,
      notes: "",
      imageIds: [],
      ...defaultValues,
    },
  })

  const notes = watch("notes") || ""

  // Count errors in each section for badges
  const basicErrors = [errors.checkedAt, errors.waterLevel].filter(Boolean).length
  const equipmentErrors = [errors.waterFlowCheck, errors.paddleWheelFunctioning].filter(Boolean).length
  const environmentalErrors = [
    errors.stressedFishAtInlets,
    errors.algalBloom,
    errors.presenceOfBirds,
  ].filter(Boolean).length
  const dischargeErrors = [
    errors.dischargePh,
    errors.dischargeDo,
    errors.dischargeFlowRate,
  ].filter(Boolean).length
  const notesErrors = [errors.procedureFollowed, errors.notes, errors.imageIds].filter(Boolean).length

  const onFormSubmit = handleSubmit(async (data) => {
    await onSubmit(data)
  })

  return (
    <form id="maintenance-form" onSubmit={onFormSubmit} className="space-y-4">
      {/* Basic Information - Always expanded */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Basic Information" defaultExpanded={true} errorCount={basicErrors}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Check Date & Time" required error={errors.checkedAt}>
              <DateTimeField
                name="checkedAt"
                control={control}
                showTime={true}
                maxDate={new Date()}
              />
            </FormField>

            <FormField label="Water Level" required error={errors.waterLevel}>
              <NumericInput
                {...register("waterLevel", { valueAsNumber: true })}
                unit="m"
                placeholder="1.5"
                hasError={!!errors.waterLevel}
                disabled={isLoading}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Equipment Status - Always expanded */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Equipment Status" defaultExpanded={true} errorCount={equipmentErrors}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <FormField label="Water Flow" error={errors.waterFlowCheck}>
              <Controller
                name="waterFlowCheck"
                control={control}
                render={({ field }) => (
                  <BooleanToggle
                    value={field.value}
                    onChange={field.onChange}
                    trueLabel="Working"
                    falseLabel="Issue"
                    disabled={isLoading}
                    hasError={!!errors.waterFlowCheck}
                  />
                )}
              />
            </FormField>

            <FormField label="Paddle Wheel" error={errors.paddleWheelFunctioning}>
              <Controller
                name="paddleWheelFunctioning"
                control={control}
                render={({ field }) => (
                  <BooleanToggle
                    value={field.value}
                    onChange={field.onChange}
                    trueLabel="Functioning"
                    falseLabel="Issue"
                    disabled={isLoading}
                    hasError={!!errors.paddleWheelFunctioning}
                  />
                )}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Environmental Observations - Always expanded */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Environmental Observations" defaultExpanded={true} errorCount={environmentalErrors}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Controller
              name="stressedFishAtInlets"
              control={control}
              render={({ field }) => (
                <CheckboxCard
                  checked={field.value}
                  onChange={field.onChange}
                  label="Stressed Fish at Inlets"
                  description="Fish showing stress behavior near water inlets"
                  warningWhenChecked={true}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="algalBloom"
              control={control}
              render={({ field }) => (
                <CheckboxCard
                  checked={field.value}
                  onChange={field.onChange}
                  label="Algal Bloom"
                  description="Visible algae accumulation in pond"
                  warningWhenChecked={true}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="presenceOfBirds"
              control={control}
              render={({ field }) => (
                <CheckboxCard
                  checked={field.value}
                  onChange={field.onChange}
                  label="Presence of Birds"
                  description="Birds observed around pond area"
                  warningWhenChecked={true}
                  disabled={isLoading}
                />
              )}
            />
          </div>
        </FormSection>
      </Card>

      {/* Discharge Parameters - Collapsed by default */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Discharge Parameters" defaultExpanded={false} errorCount={dischargeErrors}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label="Discharge pH" error={errors.dischargePh} hint="Optional">
              <NumericInput
                {...register("dischargePh", {
                  setValueAs: (v: string) => v === "" ? undefined : Number(v)
                })}
                placeholder="7.0"
                hasError={!!errors.dischargePh}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Discharge DO" error={errors.dischargeDo} hint="Optional">
              <NumericInput
                {...register("dischargeDo", {
                  setValueAs: (v: string) => v === "" ? undefined : Number(v)
                })}
                unit="mg/L"
                placeholder="6.0"
                hasError={!!errors.dischargeDo}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Flow Rate" error={errors.dischargeFlowRate} hint="Optional">
              <NumericInput
                {...register("dischargeFlowRate", {
                  setValueAs: (v: string) => v === "" ? undefined : Number(v)
                })}
                unit="L/s"
                placeholder="10.0"
                hasError={!!errors.dischargeFlowRate}
                disabled={isLoading}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Notes & Images - Collapsed by default */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Notes & Images" defaultExpanded={false} errorCount={notesErrors}>
          <div className="space-y-4">
            <FormField label="Procedure Followed" error={errors.procedureFollowed} hint="Optional SOP reference">
              <input
                type="text"
                {...register("procedureFollowed")}
                placeholder="e.g., Daily Maintenance SOP v2.1"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.procedureFollowed
                    ? "border-red-300 focus:ring-red-500/20 focus:border-red-500"
                    : "border-slate-200/50 focus:ring-emerald-500/20 focus:border-emerald-500"
                }`}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Notes" error={errors.notes} hint="Optional observations">
              <TextAreaField
                {...register("notes")}
                placeholder="Any additional observations, issues noted, or actions taken..."
                maxLength={2000}
                currentLength={notes.length}
                rows={4}
                hasError={!!errors.notes}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Images" error={errors.imageIds} hint="Up to 5 images, max 10MB each">
              <Controller
                name="imageIds"
                control={control}
                render={({ field }) => (
                  <ImageUploadField
                    value={field.value || []}
                    onChange={field.onChange}
                    maxFiles={5}
                    disabled={isLoading}
                  />
                )}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>
    </form>
  )
}
